import {
  fighterOf,
  motionStyle,
  opponentOf,
  pieceById,
  type BattleEvent,
  type BattleState,
  type CharacterDef,
  type FighterId,
} from '@archon3d/core';
import { shotFlavour } from '../game/projectileFlavour';
import type { SessionSnapshot } from '../game/session';
import type { ArenaStageEvent } from '../scenes/arena/arenaStage';
import { AmbiencePlayer, TABLE_AMBIENCE } from './ambience';
import { stationOn, type RadioState } from './radio';
import type { PieceMotionEvent } from '../scenes/board/pieces';
import {
  arenaFootfall,
  auraWhoosh,
  burnCrackle,
  burstBlast,
  countTick,
  fighterDeath,
  fighterHit,
  fightStart,
  meleeSwing,
  projectileImpact,
  projectileLaunch,
  suddenDeathAlarm,
} from './arenaSounds';
import type { AudioBus, Vec3Like } from './audioBus';
import {
  blinkShimmer,
  boardFootfall,
  captureDrums,
  floatHum,
  gemTap,
  newGame,
  pieceSelected,
  wingBeat,
  type SustainedSound,
} from './boardSounds';
import { spellChime } from './spellSounds';
import { fightResolved, outcomeSting, type Outcome } from './stings';
import { rand } from './synth';
import { utter, type UtteranceKind, type UtteranceOptions } from './voice';
import { voiceProfileFor, type VoiceProfile } from './voiceProfile';

/** The parts of the game the director listens to; narrow so tests can fake them. */
export interface SoundSession {
  subscribe(listener: () => void): () => void;
  getSnapshot(): SessionSnapshot;
}

export interface SoundPieces {
  onMotion(listener: (event: PieceMotionEvent) => void): () => void;
  positionOf(pieceId: number): Vec3Like | undefined;
}

export interface SoundArena {
  onEvent(listener: (event: ArenaStageEvent) => void): () => void;
}

/** What the ambience follows of the radio: whether a station is on, and the Music slider. */
export interface MusicSource {
  get(): Pick<RadioState, 'status' | 'volume'>;
  subscribe(listener: () => void): () => void;
}

export interface SoundDirectorDeps {
  bus: AudioBus;
  session: SoundSession;
  pieces: SoundPieces;
  arena: SoundArena;
  /** The arenas' ambience; tests hand in their own. */
  ambience?: Pick<AmbiencePlayer, 'play' | 'dispose'> & Partial<Pick<AmbiencePlayer, 'setVolume'>>;
  /**
   * The radio: while a station is on the ambience is silent, and it follows the Music slider
   * (the owner, 2026-09-26). Without one the ambience plays as if the radio were off.
   */
  music?: MusicSource;
  /** The loop the table plays between duels; `TABLE_AMBIENCE` unless a test names another. */
  tableAmbience?: string | null;
}

export interface SoundDirector {
  /** A gem on a tablet was tapped; `enabled` false for a dead gem. */
  gemTap(gem: { enabled: boolean }): void;
  dispose(): void;
}

/** Seconds before the same speaker may utter again, so a barrage does not become a chorus. */
const UTTERANCE_GAP = 0.5;
/** Seconds between an opponent's idle mutters in the arena. */
const TAUNT_GAP: [number, number] = [5, 12];
const ATTACK_SHOUT_CHANCE = 0.4;
/** Seconds of the duel between two crackles of the same burning fighter. */
const CRACKLE_GAP = 0.35;
const SELECT_MUTTER_CHANCE = 0.5;

/**
 * Turns what happens in the game into sound: session events (spells, captures, endings),
 * the pieces' journeys (footfalls in step with the animation), the tablets' gems and the duel
 * (swings, shots, hits and the voices of the fighters). It only listens and plays; nothing in
 * the game waits for it, and a sound that fails is logged once and otherwise ignored.
 */
export function attachSoundDirector(deps: SoundDirectorDeps): SoundDirector {
  const { bus, session, pieces, arena } = deps;
  const roster = session.getSnapshot().ctx.roster;
  const profiles = new Map<string, VoiceProfile>();
  const hums = new Map<number, SustainedSound>();
  const lastSpoke = new Map<string, number>();
  let previous = session.getSnapshot();
  let lastEventId = previous.lastEvent?.id ?? 0;
  let lastSelected = previous.selectedPieceId;
  /** Whether the pending capture was handed to the arena, where its death cry was heard. */
  let inArena = previous.arena !== null;
  let nextTauntAt = Infinity;
  /** When each fighter last crackled in the fire, in the duel's seconds. */
  const crackledAt = new Map<FighterId, number>();
  let warned = false;
  /** The arena's own sound under the fight: its theme's recorded loop (audit step 26b). */
  const ambience = deps.ambience ?? new AmbiencePlayer(bus);
  /**
   * The table's own sound between duels, the wind round the temple, where there was silence under
   * the event sounds (x-aaa-gap#19). It starts as soon as a gesture has let sound play, and is
   * asked for again on each change of the game; the arena's loop takes over in a duel and gives it
   * back after.
   */
  const tableAmbience = deps.tableAmbience === undefined ? TABLE_AMBIENCE : deps.tableAmbience;
  /** The arena has the picture, and its own loop (or none) plays: `arenaAmbience`. */
  let arenaSounding = false;
  let arenaAmbience: string | null = null;
  /**
   * The tab is hidden, or the headset is off: no loop plays to nobody. The radio stops there too,
   * and the table's loop would otherwise go on for as long as the tab stays open.
   */
  const page = typeof document === 'undefined' ? null : document;
  let hidden = page?.hidden ?? false;
  const { music } = deps;
  /** A station on: the radio is the music then, and the wind and the birds give way to it. */
  const radioOn = () => (music ? stationOn(music.get()) : false);
  const settleAmbience = () =>
    ambience.play(hidden || radioOn() ? null : arenaSounding ? arenaAmbience : tableAmbience);
  const followMusic = () => {
    if (music) ambience.setVolume?.(music.get().volume);
  };
  followMusic();
  const watchPage = (listener: () => void) => {
    page?.addEventListener('visibilitychange', listener);
    return () => page?.removeEventListener('visibilitychange', listener);
  };

  const guard =
    <A extends unknown[]>(fn: (...args: A) => void) =>
    (...args: A): void => {
      try {
        fn(...args);
      } catch (err) {
        // A sound must never break the game; one warning is enough to find the culprit.
        if (!warned) {
          warned = true;
          console.warn('sound failed', err);
        }
      }
    };

  const character = (id: string): CharacterDef | undefined => roster[id];
  /** 0 for a pawn … 1 for a leader: the size of the body, for footfalls and thuds. */
  const weightOf = (id: string) => ((character(id)?.tier ?? 1) - 1) / 4;
  const profile = (id: string): VoiceProfile => {
    let p = profiles.get(id);
    if (!p) {
      p = voiceProfileFor(character(id) ?? { id, tier: 1 });
      profiles.set(id, p);
    }
    return p;
  };
  /** Utters in a character's voice unless the same speaker spoke a moment ago. */
  const speak = (
    speaker: string,
    characterId: string,
    kind: UtteranceKind,
    options: UtteranceOptions,
    force = false,
  ) => {
    const now = bus.now;
    if (!force && (lastSpoke.get(speaker) ?? -Infinity) + UTTERANCE_GAP > now) return;
    lastSpoke.set(speaker, now);
    utter(bus, profile(characterId), kind, options);
  };

  const onSession = guard(() => {
    settleAmbience();
    const snap = session.getSnapshot();
    if (snap.selectedPieceId !== lastSelected) {
      lastSelected = snap.selectedPieceId;
      if (snap.selectedPieceId !== null) {
        const piece = pieceById(snap.state, snap.selectedPieceId);
        const at = pieces.positionOf(snap.selectedPieceId);
        pieceSelected(bus, at);
        if (piece && Math.random() < SELECT_MUTTER_CHANCE)
          speak(`board:${piece.id}`, piece.character, 'idle', { at, gain: 0.35 });
      }
    }
    if (snap.arena) inArena = true;
    const event = snap.lastEvent;
    if (event && event.id !== lastEventId) {
      lastEventId = event.id;
      /** The drums of a capture and the attacker's war cry, `delay` seconds on. */
      const announceFight = (delay: number) => {
        captureDrums(bus);
        inArena = false;
        const pending = snap.state.pendingBattle;
        const attacker = pending ? pieceById(snap.state, pending.attackerId) : undefined;
        if (attacker)
          speak(
            `board:${attacker.id}`,
            attacker.character,
            'attack',
            { at: pieces.positionOf(attacker.id), delay },
            true,
          );
      };
      switch (event.type) {
        case 'spell':
          if (snap.lastMove?.type === 'spell') spellChime(bus, snap.lastMove.spell);
          // A summoned elemental attacks at once: its fight is announced like any capture,
          // after the chime has had its say.
          if (snap.state.phase === 'battle') announceFight(0.8);
          break;
        case 'battle':
          // A muster's answers are 'battle' events too. The drums and the war cry are the
          // attack's alone; a fighter that joins gives its own cry (client-audio-engine:bugs#4).
          if (snap.lastMove?.type === 'pass-battle') break;
          if (snap.lastMove?.type === 'join-battle') {
            const joined = pieceById(snap.state, snap.lastMove.pieceId);
            if (joined)
              speak(
                `board:${joined.id}`,
                joined.character,
                'attack',
                { at: pieces.positionOf(joined.id) },
                true,
              );
            break;
          }
          announceFight(0.3);
          break;
        case 'battle-resolved':
        case 'ended': {
          const battle = previous.state.pendingBattle;
          if (battle) {
            if (event.type === 'battle-resolved') fightResolved(bus);
            // Fought in the arena, the loser's cry was heard there; rolled, it dies on the board.
            if (!inArena)
              for (const id of battle.participantIds ?? [battle.attackerId, battle.defenderId]) {
                if (pieceById(snap.state, id)) continue;
                const fallen = pieceById(previous.state, id);
                if (fallen)
                  speak(
                    `board:${id}`,
                    fallen.character,
                    'death',
                    { at: pieces.positionOf(id), gain: 0.45 },
                    true,
                  );
              }
          }
          if (event.type === 'ended') outcomeSting(bus, gameOutcome(snap), 'game');
          inArena = false;
          break;
        }
        case 'new-game':
          newGame(bus);
          break;
        case 'move':
          break;
      }
    }
    previous = snap;
  });

  const onMotion = guard((e: PieceMotionEvent) => {
    const weight = weightOf(e.character);
    switch (e.style) {
      case 'walk':
        if (e.kind === 'step') boardFootfall(bus, e.at, weight, e.step);
        else if (e.kind === 'arrive') boardFootfall(bus, e.at, Math.min(1, weight + 0.3), e.step);
        break;
      case 'jump':
        if (e.kind !== 'depart') boardFootfall(bus, e.at, Math.min(1, weight + 0.4), e.step);
        break;
      case 'fly':
        wingBeat(bus, e.at, weight);
        if (e.kind === 'arrive') boardFootfall(bus, e.at, weight, 0);
        break;
      case 'float':
        if (e.kind === 'depart') {
          hums.get(e.pieceId)?.stop();
          const hum = floatHum(bus, e.at);
          if (hum) hums.set(e.pieceId, hum);
          else hums.delete(e.pieceId);
        } else if (e.kind === 'step') hums.get(e.pieceId)?.place(e.at);
        else {
          hums.get(e.pieceId)?.stop();
          hums.delete(e.pieceId);
        }
        break;
      case 'blink':
        if (e.kind === 'depart') blinkShimmer(bus, e.at, 'out');
        else if (e.kind === 'arrive') blinkShimmer(bus, e.at, 'in');
        break;
    }
  });

  const onArena = guard((e: ArenaStageEvent) => {
    const { state, humans } = e;
    const fighter = (id: FighterId) => fighterOf(state, id);
    // One fixed camera watches the duel for everyone, so each fighter is heard where it stands
    // in the world, the player's own too (client-audio-engine:bugs#1).
    const at = (id: FighterId): Vec3Like => e.toWorld(fighter(id).pos);
    const own = (id: FighterId) => humans.includes(id);
    switch (e.type) {
      case 'ambience':
        arenaSounding = !e.leaving;
        arenaAmbience = e.leaving ? null : e.file;
        settleAmbience();
        break;
      case 'phase':
        if (e.phase === 'fighting') {
          fightStart(bus);
          nextTauntAt = state.time + rand(...TAUNT_GAP);
          crackledAt.clear();
        } else {
          nextTauntAt = Infinity;
          const result = state.result;
          // Every seat, not the first two: a third seat's win is a win (client-audio-engine:bugs#9).
          const winner = state.seats.find((id) => fighter(id).pieceId === result?.winnerId);
          outcomeSting(bus, duelOutcome(winner, humans), 'fight');
          if (winner)
            speak(
              `arena:${winner}`,
              fighter(winner).character,
              'victory',
              { at: at(winner), delay: 0.35 },
              true,
            );
        }
        break;
      case 'count':
        countTick(bus, e.n);
        break;
      case 'step': {
        const f = fighter(e.fighter);
        const def = character(f.character);
        if (def && motionStyle(def) === 'fly') wingBeat(bus, at(e.fighter), weightOf(f.character));
        else arenaFootfall(bus, at(e.fighter), weightOf(f.character), own(e.fighter));
        break;
      }
      case 'clock':
        if (state.time >= nextTauntAt) {
          nextTauntAt = state.time + rand(...TAUNT_GAP);
          const talkers = state.seats.filter((id) => !own(id) && fighter(id).alive);
          const id = talkers[Math.floor(Math.random() * talkers.length)];
          if (id) speak(`arena:${id}`, fighter(id).character, 'idle', { at: at(id), gain: 0.5 });
        }
        break;
      case 'battle':
        onBattle(e.event, state, own, at, e.toWorld);
        break;
    }
  });

  const onBattle = (
    event: BattleEvent,
    state: BattleState,
    own: (id: FighterId) => boolean,
    at: (id: FighterId) => Vec3Like,
    toWorld: ArenaStageEvent['toWorld'],
  ) => {
    const flavourOf = (id: FighterId) =>
      shotFlavour(
        character(fighterOf(state, id).character),
        character(fighterOf(state, opponentOf(state, id)).character),
      );
    switch (event.type) {
      case 'attack': {
        const f = fighterOf(state, event.fighter);
        const where = at(f.id);
        switch (event.kind) {
          case 'projectile':
            projectileLaunch(bus, where, flavourOf(f.id));
            break;
          case 'aura':
            // The wail is the attack: always in the character's own voice.
            auraWhoosh(bus, where);
            speak(`arena:${f.id}`, f.character, 'attack', { at: where }, true);
            return;
          case 'burst':
            burstBlast(bus, where);
            break;
          default:
            // A blow's swing and shout came with its wind-up (D-42): it lands in silence.
            return;
        }
        if (Math.random() < ATTACK_SHOUT_CHANCE)
          speak(`arena:${f.id}`, f.character, 'attack', {
            at: where,
            gain: own(f.id) ? 0.4 : 0.6,
          });
        break;
      }
      case 'windup': {
        // An aura's wail and a burst's blast are their landing; a blow swings as it winds up.
        if (event.kind === 'aura' || event.kind === 'burst') break;
        const f = fighterOf(state, event.fighter);
        const where = at(f.id);
        meleeSwing(bus, where, weightOf(f.character));
        if (Math.random() < ATTACK_SHOUT_CHANCE)
          speak(`arena:${f.id}`, f.character, 'attack', {
            at: where,
            gain: own(f.id) ? 0.4 : 0.6,
          });
        break;
      }
      case 'hit': {
        // The ground burns every step: it crackles every few tenths of a second instead, where a
        // thud and a grunt thirty times a second would drown the fight. A burst's cost is its
        // owner's own, and the flash shows it.
        if (event.source === 'hazard') {
          const last = crackledAt.get(event.target) ?? -Infinity;
          if (state.time - last >= CRACKLE_GAP || state.time < last) {
            crackledAt.set(event.target, state.time);
            burnCrackle(bus, at(event.target));
          }
          break;
        }
        if (event.source === 'cost') break;
        const target = fighterOf(state, event.target);
        const by = fighterOf(state, event.by);
        const where = at(target.id);
        const severity = Math.min(1, event.damage / Math.max(1, target.maxHp));
        if (by.attackType === 'projectile' && by.id !== target.id)
          projectileImpact(bus, where, flavourOf(by.id));
        fighterHit(bus, where, severity);
        if (target.alive)
          speak(`arena:${target.id}`, target.character, 'hit', { at: where, intensity: severity });
        break;
      }
      case 'projectile-hit-world':
        projectileImpact(bus, toWorld(event.at), flavourOf(event.owner));
        break;
      case 'death': {
        const f = fighterOf(state, event.fighter);
        const where = at(f.id);
        fighterDeath(bus, where, weightOf(f.character));
        speak(`arena:${f.id}`, f.character, 'death', { at: where }, true);
        break;
      }
      case 'sudden-death':
        suddenDeathAlarm(bus);
        break;
    }
  };

  const unsubscribes = [
    session.subscribe(onSession),
    pieces.onMotion(onMotion),
    arena.onEvent(onArena),
    bus.onStarted(guard(settleAmbience)),
    music?.subscribe(
      guard(() => {
        followMusic();
        settleAmbience();
      }),
    ) ?? (() => {}),
    watchPage(
      guard(() => {
        hidden = page?.hidden ?? false;
        settleAmbience();
      }),
    ),
  ];
  // A scene is built once per game; when the bus already runs, this is a game the player started.
  guard(() => {
    if (bus.running) newGame(bus);
  })();

  return {
    gemTap: guard((gem: { enabled: boolean }) => gemTap(bus, gem.enabled)),
    dispose() {
      for (const unsubscribe of unsubscribes) unsubscribe();
      for (const hum of hums.values()) hum.stop();
      hums.clear();
      ambience.dispose();
    },
  };
}

/**
 * A duel's end as the people at this device hear it. One person hears their own win or loss.
 * Two or more hear the fight decided without taking a side, as the HUD names the winner
 * (client-audio-engine:quality#4). Watching AIs, someone's win is a win.
 */
export function duelOutcome(winner: FighterId | undefined, humans: readonly FighterId[]): Outcome {
  if (!winner) return 'draw';
  if (humans.length === 0) return 'win';
  if (humans.length > 1) return 'decided';
  return humans[0] === winner ? 'win' : 'lose';
}

/** The game's end as the local player hears it; watching two AIs, someone's win is a win. */
export function gameOutcome(snap: SessionSnapshot): Outcome {
  const { state, seats } = snap;
  if (!state.winner || state.winner === 'draw') return 'draw';
  const humans = Object.values(seats).filter((seat) => seat === 'human').length;
  if (humans === 0) return 'win';
  return seats[state.winner] === 'human' ? 'win' : 'lose';
}
